import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, XCircle } from 'lucide-react';
import { Skeleton } from '../../../shared/components/Skeleton';
import { Alert } from '../../../shared/components/Alert';
import { Button } from '../../../shared/components/Button';
import { EmptyState } from '../../../shared/components/EmptyState';
import { useToast } from '../../../shared/components/Toast';
import { usePendentesAprovacao } from '../hooks';
import { SituacaoBadge } from '../components/BaseComercialBadges';
import {
  AprovarBaseModal,
  RejeitarBaseModal,
} from '../components/BaseComercialActionModals';
import { formatCurrency, formatVigencia } from '../utils/format';
import type { BaseComercialListItem } from '../api';

/**
 * Fila de aprovações da Base Comercial (task 11.3) — para o Gerente
 * Comercial. Lista as Bases aguardando aprovação, com resumo das
 * condições e ações aprovar/rejeitar.
 */
const PAGE_SIZE = 20;

export default function AprovacoesBaseComercialPage(): React.ReactElement {
  const navigate = useNavigate();
  const toast = useToast();
  const [page, setPage] = useState(0);
  const { data, isLoading, isError, error } = usePendentesAprovacao({
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });
  const [selecionada, setSelecionada] = useState<BaseComercialListItem | null>(null);
  const [modal, setModal] = useState<'aprovar' | 'rejeitar' | null>(null);

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-2/3 mb-4" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }
  if (isError) {
    return (
      <div className="p-6 space-y-6">
        <Alert variant="error">
          Erro ao carregar fila de aprovações:{' '}
          {(error as Error)?.message ?? 'Falha desconhecida.'}
        </Alert>
      </div>
    );
  }

  const pendentes = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPaginas = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <button
        onClick={() => navigate('/base-comercial')}
        className="mb-4 inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
      >
        <ArrowLeft className="h-4 w-4" /> Voltar para a lista
      </button>

      <div className="mb-4">
        <h1 className="text-xl font-semibold text-gray-900">Aprovações de Base Comercial</h1>
        <p className="mt-1 text-sm text-gray-600">
          Bases aguardando aprovação do Gerente Comercial.
        </p>
      </div>

      {pendentes.length === 0 ? (
        <EmptyState
          title="Nenhuma aprovação pendente"
          description="Quando uma Base for encaminhada para aprovação, ela aparece aqui."
        />
      ) : (
        <>
        <div className="space-y-3">
          {pendentes.map((b) => (
            <div
              key={b.id}
              className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-xs text-gray-600">{b.codigo}</span>
                    <SituacaoBadge situacao={b.situacao} />
                  </div>
                  <h3
                    className="mt-1 cursor-pointer text-base font-semibold text-gray-900 hover:text-blue-700"
                    onClick={() => navigate(`/base-comercial/${b.id}`)}
                  >
                    {b.nome}
                  </h3>
                  <p className="mt-1 text-sm text-gray-600">
                    {b.modalidade} • {formatVigencia(b.dataInicio, b.dataFim)}
                  </p>
                  <p className="mt-1 text-sm text-gray-700">
                    <span className="font-medium">Valor total:</span>{' '}
                    {formatCurrency(b.valorTotal, b.moeda)}
                  </p>
                </div>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      setSelecionada(b);
                      setModal('aprovar');
                    }}
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    Aprovar
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => {
                      setSelecionada(b);
                      setModal('rejeitar');
                    }}
                  >
                    <XCircle className="h-3.5 w-3.5" />
                    Rejeitar
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
        {total > PAGE_SIZE && (
          <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
            <span>
              {total} pendente(s) · página {page + 1} de {totalPaginas}
            </span>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                Anterior
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage((p) => p + 1)}
                disabled={page + 1 >= totalPaginas}
              >
                Próxima
              </Button>
            </div>
          </div>
        )}
        </>
      )}

      {selecionada && (
        <>
          <AprovarBaseModal
            base={selecionada}
            isOpen={modal === 'aprovar'}
            onClose={() => setModal(null)}
            onSuccess={() => toast.success(`${selecionada.codigo} aprovada.`)}
          />
          <RejeitarBaseModal
            base={selecionada}
            isOpen={modal === 'rejeitar'}
            onClose={() => setModal(null)}
            onSuccess={() => toast.info(`${selecionada.codigo} rejeitada.`)}
          />
        </>
      )}
    </div>
  );
}
